// timezone.ts — 사용자가 고른 timezone 으로 시각을 표시하기 위한 순수 헬퍼 + localStorage glue.
// 백엔드는 시각을 UTC ISO 문자열로 내려주고, 화면 표시는 전적으로 이 모듈에서 변환한다.
// (theme.ts 와 동일한 패턴: 순수 로직 + 얇은 storage glue, React 의존성 없음.)

export const TIMEZONE_STORAGE_KEY = "jw-cms-timezone";
export const DEFAULT_TIMEZONE = "Asia/Seoul";

// 드롭다운에 노출할 주요 timezone — value 는 IANA 식별자, label 은 한국어 + UTC offset 힌트.
export const TIMEZONES: { value: string; label: string }[] = [
  { value: "Asia/Seoul", label: "서울 (UTC+9)" },
  { value: "Asia/Tokyo", label: "도쿄 (UTC+9)" },
  { value: "Asia/Shanghai", label: "상하이 (UTC+8)" },
  { value: "Asia/Singapore", label: "싱가포르 (UTC+8)" },
  { value: "Asia/Kolkata", label: "인도 (UTC+5:30)" },
  { value: "Asia/Dubai", label: "두바이 (UTC+4)" },
  { value: "Europe/London", label: "런던 (UTC+0/+1)" },
  { value: "Europe/Berlin", label: "베를린 (UTC+1/+2)" },
  { value: "UTC", label: "UTC (UTC+0)" },
  { value: "America/New_York", label: "뉴욕 (UTC-5/-4)" },
  { value: "America/Chicago", label: "시카고 (UTC-6/-5)" },
  { value: "America/Los_Angeles", label: "로스앤젤레스 (UTC-8/-7)" },
];

// Intl 에 zone 을 넘겨 예외가 나는지로 유효성 판정 — 알 수 없는 zone 은 throw 한다.
export function isValidTimezone(tz: string): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

// 브라우저가 보고하는 zone. 알 수 없으면 기본값으로 떨어진다.
export function detectTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && isValidTimezone(tz) ? tz : DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

// 저장된 선호값이 유효하면 그것을, 아니면 브라우저 감지값을 쓴다.
export function resolveInitialTimezone(stored: string | null): string {
  if (stored && isValidTimezone(stored)) return stored;
  return detectTimezone();
}

// UTC ISO 문자열을 주어진 zone 기준 "YYYY-MM-DD HH:mm" 로 변환.
// 빈 입력은 "—", 파싱 불가한 입력은 원본을 그대로 반환(데이터 유실 방지).
export function formatInTimeZone(iso: string, tz: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const zone = isValidTimezone(tz) ? tz : detectTimezone();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}`;
}

// ---- storage glue (localStorage 는 Safari private 모드 등에서 throw 할 수 있어 best-effort) ----

export function readStoredTimezone(): string | null {
  try {
    return window.localStorage.getItem(TIMEZONE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function persistTimezone(tz: string): void {
  try {
    window.localStorage.setItem(TIMEZONE_STORAGE_KEY, tz);
  } catch {
    // best-effort only
  }
}
